import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ServiceRequestService } from "./serviceRequestService";
import { ServiceRequestPage } from "./serviceRequestPage";
import { ServiceRequestView } from "./serviceRequestView";
import { ServiceRequestForm } from "./serviceRequestForm";
import { useSnackbar } from "../../assets/vfx/snackbar/vfxSnackbarContext";
import { MessageSeverity } from "../../assets/utils/lib/messageSeverity";
import { GRID_ACTION_TYPES, GridActionType } from "../../assets/types";
import { isGridActionType } from "../../assets/utils/helpers";
import { ServiceRequestCommentDto, ServiceRequestDto } from "../../services/dto";
import { Dialog, LoadingIndicator } from "../../assets/vfx";
import { Constants } from "../../assets/utils/lib/constants";
import { ServiceRequestNotesForm } from "./serviceRequestNotesForm";
import { useSelector } from "react-redux";
import useServiceRequestStyles from "./styles";

export enum Priority {
  High = "10bcbeab-642e-f111-8342-e82a44f01ae8",
  Medium = "11bcbeab-642e-f111-8342-e82a44f01ae8",
  Low = "12bcbeab-642e-f111-8342-e82a44f01ae8",
}

export enum RequestStatus {
  Open = "0dbcbeab-642e-f111-8342-e82a44f01ae8",
  InProgress = "0ebcbeab-642e-f111-8342-e82a44f01ae8",
  Closed = "0fbcbeab-642e-f111-8342-e82a44f01ae8",
}

export const ServiceRequestContainer = () => {
  const isServiceRequestEnabledFromStore = useSelector((state: any) => state.layout.isServiceRequestEnabled);
  const isServiceRequestEnabled = isServiceRequestEnabledFromStore === true;
  const isLoadingServiceRequestEnabled = isServiceRequestEnabledFromStore === undefined;

  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const [selectedId, setSelectedId] = useState<string>(Constants.emptyGuid);
  const [drawerType, setDrawerType] = useState<GridActionType>(GRID_ACTION_TYPES.HIDDEN);
  const [showCloseDialog, setShowCloseDialog] = useState<boolean>(false);
  const [showCompleteRequestDialog, setShowCompleteRequestDialog] = useState<boolean>(false);

  const {
    data: detailsData,
    isLoading: isLoadingDetails,
    refetch: refetchDetails,
  } = ServiceRequestService.getServiceRequestQuery(selectedId, isServiceRequestEnabled);

  const { data: lookups, isLoading: isLoadingLookups } = ServiceRequestService.getLookupsQuery(isServiceRequestEnabled);

  const {
    data: dashboardCounts,
    isLoading: isLoadingDashboardCounts,
    refetch: refetchDashboardCounts,
  } = ServiceRequestService.dashboardCountsQuery(isServiceRequestEnabled);

  const { mutateAsync: saveRequest } = ServiceRequestService.save();
  const { mutateAsync: addNote } = ServiceRequestService.addNote();
  const { mutate: completeRequest } = ServiceRequestService.completeRequest();
  const { mutate: closeRequest } = ServiceRequestService.closeRequest();

  const refreshData = () => {
    queryClient.invalidateQueries({ queryKey: ["serviceRequestGridQuery"] });
    queryClient.invalidateQueries({ queryKey: ["serviceRequestDashboardCountsQuery"] });
    if (selectedId !== Constants.emptyGuid) {
      refetchDetails();
      refetchDashboardCounts();
    }
  };

  const handleHide = () => {
    setDrawerType(GRID_ACTION_TYPES.HIDDEN);
  };

  const handleGridAction = (actionType: string, dataId: string): void => {
    if (isGridActionType(actionType)) {
      setDrawerType(actionType);
      setSelectedId(dataId);
    }
  };

  const onAddNoteSubmit = async (data: ServiceRequestCommentDto) => {
    return await addNote(data, {
      onSuccess: (res) => {
        if (!res.errorMessages?.length) {
          showSnackbar("Note added successfully", MessageSeverity.Success);
          refreshData();
          handleHide();
        }
      },
    });
  };

  const onConfirmCloseRequest = async () => {
    if (!selectedId) return;
    completeRequest(selectedId, {
      onSuccess: () => {
        showSnackbar("Request closed", MessageSeverity.Success);
        refreshData();
        handleHide();
        setShowCloseDialog(false);
      },
    });
  };

  const onConfirmCompleteRequest = () => {
    if (!selectedId) return;
    closeRequest(selectedId, {
      onSuccess: () => {
        showSnackbar("Request marked as completed", MessageSeverity.Success);
        refreshData();
        handleHide();
        setShowCompleteRequestDialog(false);
      },
    });
  };

  const handleAddNew = () => {
    setSelectedId(Constants.emptyGuid);
    setDrawerType(GRID_ACTION_TYPES.NEW);
  };

  const handleSaveForm = async (payload: ServiceRequestDto) => {
    return await saveRequest(payload, {
      onSuccess: (res) => {
        if (res.errorMessages?.length) {
          showSnackbar(res.errorMessages[0] || "Error saving", MessageSeverity.Error);
        } else {
          showSnackbar("Service request submitted successfully", MessageSeverity.Success);
          handleHide();
          refreshData();
        }
      },
      onError: () => {
        showSnackbar("Failed to submit service request", MessageSeverity.Error);
      },
    });
  };

  const handleOpenNotes = () => {
    setDrawerType(GRID_ACTION_TYPES.NOTES);
  };

  const renderDrawerContent = (): React.JSX.Element | null => {
    switch (drawerType) {
      case GRID_ACTION_TYPES.NEW:
        return (
          <ServiceRequestForm
            show={true}
            onHide={handleHide}
            onSave={handleSaveForm}
            lookups={lookups}
            isLoadingLookups={isLoadingLookups}
          />
        );
      case GRID_ACTION_TYPES.EDIT:
      case GRID_ACTION_TYPES.VIEW:
      case GRID_ACTION_TYPES.DOUBLE_CLICK:
        return (
          <ServiceRequestView
            show={true}
            data={detailsData}
            lookups={lookups}
            onHide={handleHide}
            onAddNote={handleOpenNotes}
            onComplete={() => setShowCompleteRequestDialog(true)}
            onClose={() => setShowCloseDialog(true)}
            isLoading={isLoadingDetails}
          />
        );
      case GRID_ACTION_TYPES.NOTES:
        return (
          <ServiceRequestNotesForm
            show={true}
            data={{
              requestId: selectedId ?? Constants.emptyGuid,
              commentById: Constants.emptyString,
              commentDate: new Date(),
              comment: Constants.emptyString
            }}
            onHide={handleHide}
            onSave={onAddNoteSubmit}
          />
        );
      default:
        return null;
    }
  };

  const styles = useServiceRequestStyles();
  const isLoading = isLoadingServiceRequestEnabled || (isServiceRequestEnabled && (isLoadingDashboardCounts || isLoadingLookups));

  if (isLoading) {
    return <LoadingIndicator isFullPage={true} />;
  }

  if (!isServiceRequestEnabled) {
    return (
      <div style={styles.disabledContainer}>
        <div style={styles.disabledCard}>
          <h1 style={styles.disabledTitle}>Service Requests</h1>
          <p style={styles.disabledText}>This feature is currently not enabled for your organization.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ServiceRequestPage
        dashboardCounts={dashboardCounts}
        show={false}
        lookups={lookups}
        isLoadingLookups={isLoadingLookups}
        onRowClick={(id) => handleGridAction(GRID_ACTION_TYPES.VIEW, id)}
        onNew={handleAddNew}
        enabled={isServiceRequestEnabled}
      />

      {renderDrawerContent()}

      <Dialog
        contentText="Are you sure you want to Close this Request?"
        contentTitle="Close Request"
        onClose={() => setShowCloseDialog(false)}
        onConfirm={onConfirmCloseRequest}
        open={showCloseDialog}
        closeText="No, Go Back"
        confirmText="Yes, Continue"
      />

      <Dialog
        contentText="Are you sure you want to Complete this Request?"
        contentTitle="Complete Request"
        onClose={() => setShowCompleteRequestDialog(false)}
        onConfirm={onConfirmCompleteRequest}
        open={showCompleteRequestDialog}
        closeText="No, Go Back"
        confirmText="Yes, Continue"
      />
    </>
  );
};
